/**
 * CENTRAL ADMISSION POLICY DEFINITIONS
 *
 * This is the single source of truth for all admission policy related constants,
 * types, labels, and utility functions. When making changes to admission policies,
 * update only this file.
 *
 * Import from this file rather than defining admission policy constants elsewhere.
 */

/**
 * Admission policy types used throughout the application
 */
export const ADMISSION_POLICIES = {
  OPEN: 'open',
  OPEN_WITH_RESTRICTIONS: 'open_with_restrictions',
  BY_REFERRAL: 'by_referral',
  CLOSED: 'closed',
} as const

/**
 * Type definition for admission policies
 */
export type AdmissionPolicyType = typeof ADMISSION_POLICIES[keyof typeof ADMISSION_POLICIES]

/**
 * Display labels for admission policies
 */
export const ADMISSION_POLICY_LABELS: Record<AdmissionPolicyType, string> = {
  [ADMISSION_POLICIES.OPEN]: 'Open',
  [ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS]: 'Open with Restrictions',
  [ADMISSION_POLICIES.BY_REFERRAL]: 'By Referral',
  [ADMISSION_POLICIES.CLOSED]: 'Closed',
}

/**
 * Badge severity levels for each policy type
 */
export const ADMISSION_POLICY_SEVERITIES: Record<AdmissionPolicyType, string> = {
  [ADMISSION_POLICIES.OPEN]: 'success',
  [ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS]: 'info',
  [ADMISSION_POLICIES.BY_REFERRAL]: 'danger',
  [ADMISSION_POLICIES.CLOSED]: 'danger',
}

/**
 * Descriptions for each admission policy
 */
export const ADMISSION_POLICY_DESCRIPTIONS: Record<AdmissionPolicyType, string> = {
  [ADMISSION_POLICIES.OPEN]: 'This SubKrewe is open to all members.',
  [ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS]: 'This SubKrewe is open, but has specific requirements for joining.',
  [ADMISSION_POLICIES.BY_REFERRAL]: 'This SubKrewe requires a referral from an existing member and approval from the captain.',
  [ADMISSION_POLICIES.CLOSED]: 'This SubKrewe requires approval from the captain to join.',
}

/**
 * Get the dropdown options for admission policies
 * @param includeAll Whether to include an "All" option (for filtering)
 * @returns Array of dropdown option objects
 */
export function getAdmissionPolicyOptions(includeAll = false): Array<{ label: string, value: string | null }> {
  const options = Object.entries(ADMISSION_POLICY_LABELS).map(([value, label]) => ({
    label,
    value,
  }))

  if (includeAll) {
    options.unshift({ label: 'All Admission Types', value: '' })
  }

  return options
}

/**
 * The admission policy choices for forms, in order from open to closed
 * @returns Array of radio option objects { value: string, label: string }
 */
export function getAdmissionPolicyRadioItems(): Array<{ value: string, label: string }> {
  // Start with all policies except OTHER
  const policies = [
    ADMISSION_POLICIES.OPEN,
    ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS,
    ADMISSION_POLICIES.BY_REFERRAL,
    ADMISSION_POLICIES.CLOSED,
  ]

  // Convert to radio items
  return policies.map(policy => ({
    value: policy,
    label: ADMISSION_POLICY_LABELS[policy],
  }))
}

/**
 * Get the admission policy label from its value
 */
export function getAdmissionPolicyLabel(policy?: string): string {
  if (!policy) return 'Unknown'
  return ADMISSION_POLICY_LABELS[policy as AdmissionPolicyType] || policy
}

/**
 * Get the badge severity for an admission policy
 */
export function getAdmissionPolicySeverity(policy?: string): string {
  if (!policy) return 'secondary'
  return ADMISSION_POLICY_SEVERITIES[policy as AdmissionPolicyType] || 'secondary'
}

/**
 * Get the description for an admission policy, with optional custom text
 */
export function getAdmissionPolicyDescription(policy?: string, customRestrictions?: string): string {
  if (!policy) return 'Contact the captain for information about joining.'

  if (policy === ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS && customRestrictions) {
    return customRestrictions
  }

  return ADMISSION_POLICY_DESCRIPTIONS[policy as AdmissionPolicyType]
    || 'Contact the captain for information about joining.'
}
